import { useState } from "react";
import { Link } from "react-router-dom";
import { FaExternalLinkAlt } from "react-icons/fa";
import { RiEBike2Line } from "react-icons/ri";
import { MdOutlineShoppingBag } from "react-icons/md";
import { useFetchRushOrdersListQuery, useTypedDispatch } from "../../store";
import { centeredTdClasses, tdClasses, thClasses } from "../../configs";
import Skeleton from "./Skeleton";
import {
  OrderType,
  RushOrder,
  RushOrderLabels,
  RushOrderType,
} from "../../types";
import Pagination from "./Pagination";
import RiderSelect from "./RiderSelect";
import OrderStatusSelect from "./OrderStatusSelect";

interface RushOrdersListProps {
  rushOrderType: RushOrderType;
}

const RushOrdersList: React.FC<RushOrdersListProps> = ({ rushOrderType }) => {
  const dispatch = useTypedDispatch();
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);

  const { data, isFetching } = useFetchRushOrdersListQuery({
    page,
    perPage,
    rushType: rushOrderType,
  });

  return (
    <div className="w-full flex flex-col mx-auto p-4 gap-4">
      <h2 className="text-2xl font-semibold text-primary">
        Commandes urgentes — {RushOrderLabels[rushOrderType]}
      </h2>

      <table className="table-fixed w-full rounded-xl mt-6">
        <colgroup>
          <col style={{ width: "8%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "8%" }} />
        </colgroup>
        <thead>
          <tr>
            <th className={thClasses}>Numéro</th>
            <th className={thClasses}>Type</th>
            <th className={thClasses}>Status</th>
            <th className={thClasses}>Restaurant</th>
            <th className={thClasses}>Référent</th>
            <th className={thClasses}>Livreur</th>
            <th className={thClasses}>Téléphone livreur</th>
            <th className={thClasses}>Date création</th>
            <th className={thClasses}>Détails</th>
          </tr>
        </thead>
        <tbody>
          {isFetching ? (
            <tr>
              <td colSpan={9}>
                <Skeleton
                  times={5}
                  className="h-10"
                />
              </td>
            </tr>
          ) : data?.data?.length ? (
            data.data.map((order: RushOrder) => (
              <tr key={order._id}>
                <td className={centeredTdClasses}>#{order.displayId}</td>
                <td className={centeredTdClasses}>
                  {order.orderType === OrderType.DELIVERY ? (
                    <RiEBike2Line className="w-6 h-6 text-primary mx-auto" />
                  ) : (
                    <MdOutlineShoppingBag className="w-6 h-6 text-primary mx-auto" />
                  )}
                </td>
                <td className={tdClasses}>
                  <OrderStatusSelect
                    orderType={order.orderType}
                    orderId={order._id}
                    value={order.status}
                  />
                </td>
                <td className={tdClasses}>
                  {order.restaurantInfo?.displayName}
                </td>
                <td className={tdClasses}>
                  {order.restaurantInfo?.referant?.fullName}
                  <div className="text-sm text-gray-500">
                    {order.restaurantInfo?.referant?.phone}
                  </div>
                </td>
                <td className={tdClasses}>
                  <RiderSelect
                    orderId={order._id}
                    value={order.rider || null}
                    placeholder="Sélectionner livreur"
                  />
                </td>
                <td className={tdClasses}>{order.rider?.phone || "—"}</td>
                <td className={tdClasses}>{order.createdAt}</td>
                <td className={centeredTdClasses}>
                  <Link
                    to={order._id}
                    className="w-full flex justify-center"
                  >
                    <FaExternalLinkAlt className="w-6 text-primary" />
                  </Link>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={9}
                className="text-center py-6 text-gray-500"
              >
                Aucune commande trouvée
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <Pagination
        paginationInfo={{
          page,
          perPage,
          count: data?.paginationInfo?.count || 1,
        }}
        setPage={setPage}
        setPerPage={setPerPage}
      />
    </div>
  );
};

export default RushOrdersList;
